/**
 * wtshuang@cs.uw.edu
 * 2018/01/14
 * The panel for the question
 */

import * as React from 'react';
import { observer } from 'mobx-react';
import { action, observable } from 'mobx';


import { Answer } from '../stores/Answer';
import { Paragraph } from '../stores/Paragraph';
import { Question } from '../stores/Question';

import { store } from '../stores/Store';
import { utils } from '../stores/Utils';
import { Token, FlagDatum } from '../stores/Interfaces';

import { ParagraphPanel } from './ParagraphPanel';
import { QuestionPanel } from './QuestionPanel';
import { Flowmap } from './Flowmap';
import { Label, Divider } from 'semantic-ui-react';

export interface InstanceProps {
    question: Question;
}


@observer
export class InstancePanel extends React.Component<InstanceProps, {}> {
    // view related
    private viewType: string;
    // define the parameters
    // the following are like stored parameters related to the panel.
    private question: Question;
    private paragraph: Paragraph;
    // answer related.
    private answers: Answer[]; // total answer saved
    //private groundtruths: Answer[]; // the groundtruths
    //private predicted: Answer; // the predicted answers
    // the ids
    private qid: string;
    private aid: number;
    private pid: number;
    // observable selected token
    @observable private selectedToken: Token|null;
    @observable private hoveredIdx: number;

    @observable private displayBlogFlags: FlagDatum[];
    @observable private toggleTokens: boolean[];


    constructor (props: InstanceProps, context: any) {
        super(props, context);
        this.viewType = 'ipanel';
        this.hoveredIdx = -1;
        this.question = this.props.question;
        this.toggleTokens = this.question.doc.map(t => true);
        this.aid = this.question.aid;
        this.pid = this.question.pid;
        this.qid = this.question.qid;
        this.paragraph = store.filterParagraph(this.aid, this.pid)[0];
        this.answers = store.filterAnswer(this.qid);
        this.selectedToken = null;
        this.selectTokenHandler = this.selectTokenHandler.bind(this);
        this.mouseoveredHandler = this.mouseoveredHandler.bind(this);
        this.mouseoutHandler = this.mouseoutHandler.bind(this);
        this.selectTokenIdxHandler = this.selectTokenIdxHandler.bind(this);
        this.mouseoutTokenIdxHandler = this.mouseoutTokenIdxHandler.bind(this);
        this.selectBlockHandler = this.selectBlockHandler.bind(this);
        this.toggleTokenHandler = this.toggleTokenHandler.bind(this);


        //this.groundtruths = this.answers.filter((a: Answer) => a.isGroundtruth);
        //this.predicted = this.answers.filter((a: Answer) => !a.isGroundtruth)[0];
        this.displayBlogFlags = [];

    }

    /**
     * Render the question bar
     */
    private renderQuestion(selectedToken: Token|null): JSX.Element {
        const elementClass = utils.genClass(this.viewType, 'qpanel', [this.qid, this.hoveredIdx]);
        return <QuestionPanel
            key={elementClass.key}
            toggleTokens= { this.toggleTokens }
            attention = { this.question.attention }
            question={ this.question }
            selectedToken = { selectedToken }
            answers={ this.answers }
            mouseoutHandler={this.mouseoutHandler}
            mouseoveredHandler={this.mouseoveredHandler}
            hoveredIdx={this.hoveredIdx}
            toggleTokenHandler = { this.toggleTokenHandler }
            selectTokenHandler = { this.selectTokenHandler }
            selectBlockHandler = { this.selectBlockHandler }
        />
    }
    

    private computeAttention(): number[][] {
        const toggles = this.toggleTokens.slice();
            return utils.transpose(this.question.attention).map((a, qidx) => {
                if (toggles[qidx]) {
                    return a.slice();
                } else {
                    return a.map((i: number) => 0);
                }
            });
    }
    /**
     * Render the paragraph block.
     */
    private renderFlowmap(selectedToken: Token|null): JSX.Element {
        const tidx = this.selectedToken === null ? -1 : this.selectedToken.idx;
        const elementClass = utils.genClass(this.viewType, 'flow-panel', 
            [this.qid, this.aid, this.pid, tidx, this.hoveredIdx]);
        const attention = this.computeAttention();

        return <div id={`question-flowmap-${this.question.qid}`}
                    className='container svg' style={{height: 100}}>
            <Flowmap key={elementClass.key}
            attention = { attention }
            question={ this.question }
            selectedToken={ this.selectedToken }
            paragraph= { this.paragraph }
            answers={ this.answers }
            mouseoutHandler={this.mouseoutHandler}
            mouseoveredHandler={this.mouseoveredHandler}
            mouseoutTokenIdxHandler = { this.mouseoutTokenIdxHandler }
            selectTokenIdxHandler = { this.selectTokenIdxHandler }
        /></div>
    }


    private renderParagraph(selectedToken: Token|null): JSX.Element {
        const tidx = this.selectedToken === null ? -1 : this.selectedToken.idx;
        const elementClass = utils.genClass(this.viewType, 'ppanel', 
            [this.qid, this.aid, this.pid, tidx, this.hoveredIdx]);
        return <ParagraphPanel
            key={elementClass.key}
            displayBlogFlags = { this.displayBlogFlags }
            qid = { this.qid }
            selectedToken = { selectedToken }
            answers = { this.answers }
            hoveredIdx={ this.hoveredIdx }
            attention = { this.question.attention }
            ptokens ={ this.paragraph.doc }
            qtokens ={ this.question.doc }
            mouseoutHandler={this.mouseoutHandler}
            mouseoveredHandler={this.mouseoveredHandler}
        />
    }

     /**
     * Render the answer labels.
     * @param answers <Answer[]> all the answers to create a label for. All the predictions + groundtruth
     */
    private renderAnswerLabels(answers: Answer[]): JSX.Element {
        return <div>
            {answers.map((answer: Answer) => {
                const elementClass = utils.genClass(this.viewType, 'answer',
                    [this.qid, String(answer.isGroundtruth), answer.span_start, answer.span_end]);

                return <Label content={`[${answer.model}] ${answer.textize()} | [F1] ${answer.perform.f1.toFixed(2)}`}
                    size='small' basic
                    key= { elementClass.key } 
                    className= { elementClass.total }
                    style={{color: answer.getColorShallow()}} />
            })}
        </div>
    }

    /**
     * Render all the information
     */
    /*
    private renderAnswer(): JSX.Element {
        const elementClass = utils.genClass(this.viewType, 'apanel', this.qid);
        return <AnswerPanel
            key={elementClass.key}
            groundtruths={this.groundtruths} 
            predicted={ this.predicted }/>
    }
    */

    /**
     * Interaction. Click on the answer tag. Passed onto Answer panel
     * @param answer: the answer clicked on
     */
    @action public selectTokenHandler (token: Token): void {
        if (this.selectedToken !== null && this.selectedToken.idx === token.idx) {
            this.selectedToken = null;
        } else {
            this.selectedToken = token;
        }
    }


    @action public selectTokenIdxHandler (idx: number): void {
        const token = this.question.doc[idx];
        this.selectedToken = token;
    }


    @action public toggleTokenHandler (idx: number): void {
        if (idx >= 0 && idx < this.toggleTokens.length) {
            this.toggleTokens[idx] = !this.toggleTokens[idx];
        }
    }



    @action public mouseoutTokenIdxHandler (): void {
        this.selectedToken = null;
    }


    @action public mouseoveredHandler(idx: number): void {
        this.hoveredIdx = idx;
    }
    @action public mouseoutHandler(): void {
        this.hoveredIdx = -1;
    }

    @action public selectBlockHandler(flags: FlagDatum[]): void {
        this.displayBlogFlags = flags;
    }

    /**
     * The main rendering function
     */
    public render(): JSX.Element {
        const elementClass = utils.genClass(this.viewType, 'qcell', this.qid);
        return <div key={ elementClass.key } className={`full-width ${elementClass.total}` }>
            <Divider />
            { this.renderAnswerLabels(this.answers) }
            <Divider />
            { this.renderQuestion(this.selectedToken) }
            { this.renderFlowmap(this.selectedToken) }
            { this.renderParagraph(this.selectedToken) }                
        </div>
    }
}


// WEBPACK FOOTER //
// ./src/components/InstancePanel.tsx